/**
 * State Change Step Handler
 * Changes the state/status of a lead or deal
 */

import { supabase } from '../../supabase';
import type {
  StepExecutionContext,
  StepExecutionResult,
  StateChangeStepConfig
} from '../../types/workflow';

export async function executeStateChange(
  context: StepExecutionContext
): Promise<StepExecutionResult> {
  try {
    const config = context.step.config as StateChangeStepConfig;

    if (!config.to_state) {
      return {
        success: false,
        error: 'Missing required field: to_state',
      };
    }

    const updates: any = {
      updated_at: new Date().toISOString(),
    };

    // CROSS-ENTITY: Se workflow Deal vuole cambiare stato del lead associato
    if (context.workflow.entity_type === 'deal' && config.target_entity_type === 'related_lead') {
      // 1. Recupera il deal per ottenere il client_id (lead)
      const { data: deal, error: dealError } = await supabase
        .from('deals')
        .select('client_id')
        .eq('id', context.entity.entity_id)
        .eq('organization_id', context.organizationId)
        .single();

      if (dealError || !deal || !deal.client_id) {
        console.error('Error fetching deal or deal has no associated client:', dealError);
        return {
          success: false,
          error: 'Deal has no associated lead/client',
        };
      }

      // 2. Verifica che il client sia effettivamente un lead
      // Dobbiamo fare 2 query separate perché stato_cliente è text e non possiamo fare join diretto
      const { data: client, error: clientError } = await supabase
        .from('clients')
        .select('stato_cliente')
        .eq('id', deal.client_id)
        .eq('organization_id', context.organizationId)
        .single();

      if (clientError || !client) {
        console.error('Error fetching client:', clientError);
        return {
          success: false,
          error: 'Associated client not found',
        };
      }

      // Fetch the client state to check if it's a lead state
      const { data: clientState, error: stateError } = await supabase
        .from('client_states')
        .select('is_lead')
        .eq('id', client.stato_cliente)
        .single();

      if (stateError || !clientState) {
        console.warn('Could not fetch client state, skipping state change');
        return {
          success: true,
          nextStepId: context.step.next_step_id,
          data: { skipped: true, reason: 'Could not determine if client is a lead' },
        };
      }

      const isLead = clientState.is_lead === true;

      if (!isLead) {
        console.warn('Associated client is not a lead (current state is not a lead state), skipping state change');
        return {
          success: true,
          nextStepId: context.step.next_step_id,
          data: { skipped: true, reason: 'Client is not a lead' },
        };
      }

      // 3. Aggiorna il lead (stesso codice della sezione lead qui sotto)
      if (config.to_pipeline_id && config.to_pipeline_id !== config.from_pipeline_id) {
        updates.pipeline_id = config.to_pipeline_id;
      }

      updates.stato_cliente = config.to_state;

      if (config.update_last_activity) {
        updates.last_activity = new Date().toISOString();
      }

      const { error } = await supabase
        .from('clients')
        .update(updates)
        .eq('id', deal.client_id)
        .eq('organization_id', context.organizationId);

      if (error) {
        console.error('Error updating related lead state:', error);
        return {
          success: false,
          error: error.message,
        };
      }

      console.log(`✅ Related lead state changed to: ${config.to_state} (from deal workflow)`);

      return {
        success: true,
        nextStepId: context.step.next_step_id,
        data: { newState: config.to_state, targetEntity: 'related_lead', leadId: deal.client_id },
      };
    }

    // Handle lead state changes (workflow entity_type = 'lead')
    if (context.workflow.entity_type === 'lead') {
      // Check if it's a pipeline change
      if (config.to_pipeline_id && config.to_pipeline_id !== config.from_pipeline_id) {
        updates.pipeline_id = config.to_pipeline_id;
      }

      // Update state within pipeline
      updates.stato_cliente = config.to_state;

      if (config.update_last_activity) {
        updates.last_activity = new Date().toISOString();
      }

      const { error } = await supabase
        .from('clients')
        .update(updates)
        .eq('id', context.entity.entity_id)
        .eq('organization_id', context.organizationId);

      if (error) {
        console.error('Error updating lead state:', error);
        return {
          success: false,
          error: error.message,
        };
      }

      console.log(`✅ Lead state changed to: ${config.to_state}`);
    }
    // Handle deal state changes
    else if (context.workflow.entity_type === 'deal') {
      updates.status = config.to_state;

      // If changing to accepted/won, set closed_at and signed_at
      if (config.to_state === 'accepted' || config.to_state === 'won') {
        updates.closed_at = new Date().toISOString();
        updates.signed_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('deals')
        .update(updates)
        .eq('id', context.entity.entity_id)
        .eq('organization_id', context.organizationId);

      if (error) {
        console.error('Error updating deal state:', error);
        return {
          success: false,
          error: error.message,
        };
      }

      console.log(`✅ Deal state changed to: ${config.to_state}`);
    }

    return {
      success: true,
      nextStepId: context.step.next_step_id,
      data: { newState: config.to_state },
    };
  } catch (error) {
    console.error('Error in stateChange handler:', error);
    return {
      success: false,
      error: (error as Error).message,
    };
  }
}
